import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  Clock,
  Eye,
  FolderOpen,
  MessageSquare,
  Search,
  Star,
  UserCircle
} from 'lucide-react';
import NavigationCarousel from '../components/NavigationCarousel';
import DarkModeToggle from '../components/DarkModeToggle';
import { useTheme } from '../contexts/ThemeContext';

const CONCEPT_CATEGORIES = [
    { id: 'all', label: 'All Concepts' },
    { id: 'architecture', label: 'Architecture' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'design', label: 'Design' },
    { id: 'sustainability', label: 'Sustainability' }
  ];

const CONCEPTS = [
    {
      id: 1,
    slug: 'ai-generated-design',
      title: 'AI Generated Design',
      subtitle: 'Explore how artificial intelligence is revolutionizing architectural design',
      category: 'design',
      image: '/assets/AI GENERATED DESIGN .png',
      views: 2340,
      duration: '15 min read',
      difficulty: 'Intermediate',
      rating: 4.8,
    topics: ['AI', 'Design', 'Innovation']
    },
    {
      id: 2,
    slug: 'international-projects',
      title: 'International Projects',
      subtitle: 'Study architectural marvels from around the world',
      category: 'architecture',
      image: '/assets/INTERNATIONAL PROJECTS.png',
      views: 1876,
      duration: '20 min read',
      difficulty: 'Beginner',
      rating: 4.9,
    topics: ['Global', 'Culture', 'History']
    },
    {
      id: 3,
    slug: 'new-age-building',
      title: 'New Age Building',
      subtitle: 'Modern construction techniques and materials',
      category: 'engineering',
      image: '/assets/Newbuilding.jpeg',
      views: 3210,
      duration: '12 min read',
      difficulty: 'Advanced',
      rating: 4.7,
    topics: ['Construction', 'Materials', 'Technology']
    },
    {
      id: 4,
    slug: 'sustainable-architecture',
      title: 'Sustainable Architecture',
      subtitle: 'Green building practices and environmental design',
      category: 'sustainability',
      image: '/assets/Green Architecture.jpeg',
      views: 2890,
      duration: '18 min read',
      difficulty: 'Intermediate',
      rating: 4.9,
    topics: ['Green Building', 'Environment', 'Energy']
    },
    {
      id: 5,
    slug: 'structural-engineering-fundamentals',
      title: 'Structural Engineering Fundamentals',
      subtitle: 'Core principles of structural design and analysis',
      category: 'engineering',
      image: '/assets/GROUNDWORK IN PROGRESS.jpeg',
      views: 4560,
      duration: '25 min read',
      difficulty: 'Advanced',
      rating: 4.8,
    topics: ['Structures', 'Analysis', 'Safety']
    },
    {
      id: 6,
    slug: 'urban-planning-concepts',
      title: 'Urban Planning Concepts',
      subtitle: 'Design principles for modern city development',
      category: 'architecture',
      image: '/assets/UrbanPlanning.jpeg',
      views: 1654,
      duration: '22 min read',
      difficulty: 'Intermediate',
      rating: 4.6,
    topics: ['Urban Design', 'City Planning', 'Infrastructure']
  }
];

const conceptSlugMap = CONCEPTS.reduce((acc, concept) => {
  acc[concept.slug] = concept.id;
  return acc;
}, {});

const Concepts = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [showConceptModal, setShowConceptModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  const menuItems = [
    { label: 'HOME', path: '/app' },
    { label: 'STUDY', path: '/app/study' },
    { label: 'CHALLENGES', path: '/app/challenges' },
    { label: 'COMMUNITY', path: '/app/community' },
    { label: 'CONCEPTS', path: '/app/concepts' },
    { label: 'PIN UP', path: '/app/pinup' },
    { label: 'PROGRESS', path: '/app/progress' },
    { label: 'PROFILE', path: '/app/profile' },
    { label: 'LOG OUT', path: '/login', isLogout: true }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const conceptParam = searchParams.get('concept');
    if (conceptParam && conceptSlugMap[conceptParam]) {
      const concept = CONCEPTS.find((item) => item.id === conceptSlugMap[conceptParam]);
      if (concept) {
        setActiveFilter(concept.category);
        setSelectedConcept(concept);
        setShowConceptModal(true);
      }
    }
  }, [searchParams]);

  const filteredConcepts = useMemo(() => {
    const lowerSearch = searchTerm.trim().toLowerCase();
    return CONCEPTS.filter((concept) => {
      const matchesCategory = activeFilter === 'all' || concept.category === activeFilter;
      const matchesSearch =
        !lowerSearch ||
        concept.title.toLowerCase().includes(lowerSearch) ||
        concept.subtitle.toLowerCase().includes(lowerSearch) ||
        concept.topics.some((topic) => topic.toLowerCase().includes(lowerSearch));
      return matchesCategory && matchesSearch;
    });
  }, [activeFilter, searchTerm]);

  const toggleSidebar = () => setIsSidebarCollapsed((prev) => !prev);

  const handleConceptClick = (concept) => {
    setSelectedConcept(concept);
    setShowConceptModal(true);
  };

  const handleNavigationClick = (item) => {
    navigate(item.path);
  };

  const getDifficultyBadge = (difficulty) => {
    if (isDarkMode) {
      switch (difficulty) {
        case 'Beginner':
          return 'bg-green-900/40 text-green-300 border border-green-700/60';
        case 'Intermediate':
          return 'bg-yellow-900/40 text-yellow-300 border border-yellow-700/60';
        case 'Advanced':
          return 'bg-red-900/40 text-red-300 border border-red-700/60';
        default:
          return 'bg-gray-800 text-gray-200 border border-gray-700';
      }
    }

    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-100 text-green-700';
      case 'Intermediate':
        return 'bg-yellow-100 text-yellow-700';
      case 'Advanced':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      <div className="fixed top-0 left-0 h-screen z-40">
        <NavigationCarousel
          menuItems={menuItems}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
          />
      </div>

      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-56'}`}>
        <div className={`${isDarkMode ? 'bg-[#1f2937]' : 'bg-[#AC5757]'} rounded-l-lg`} style={{ marginLeft: isSidebarCollapsed ? '10px' : '10px', transition: 'margin-left 0.3s ease' }}>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h1 className="font-oswald font-medium text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[65px] text-white">
                CONCEPTS
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <DarkModeToggle size="small" />
              <button
                onClick={() => navigate('/app/messages')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-[#8A4A4A]'
                }`}
                aria-label="Open messages"
              >
                <MessageSquare size={20} className="text-white" />
              </button>
              <button
                onClick={() => navigate('/app/profile')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-[#8A4A4A]'
                }`}
                aria-label="Open profile"
              >
                <UserCircle size={20} className="text-white" />
              </button>
            </div>
          </div>
        </div>

        <div
          className="max-w-7xl px-6 py-8"
          style={{
            marginLeft: isSidebarCollapsed ? '20px' : '20px',
            transition: 'margin-left 0.3s ease'
          }}
        >
          <div
            className={`rounded-2xl border mb-6 p-4 md:p-6 ${
              isDarkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1 relative">
                <Search
                  size={18}
                  className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search concepts, topics, categories..."
                  className={`w-full rounded-xl pl-11 pr-4 py-3 text-sm font-medium outline-none transition-colors ${
                    isDarkMode
                      ? 'bg-gray-900/60 border border-gray-700 text-gray-100 placeholder:text-gray-500 focus:border-[#AC5757]'
                      : 'bg-gray-50 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-[#AC5757]'
                  }`}
                />
              </div>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors min-w-[200px] justify-between ${
                    isDarkMode
                      ? 'bg-gray-900/60 border border-gray-700 text-gray-200 hover:bg-gray-900/80'
                      : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span>{CONCEPT_CATEGORIES.find((cat) => cat.id === activeFilter)?.label || 'All Concepts'}</span>
                  <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              {isDropdownOpen && (
                  <div
                    className={`absolute right-0 top-full mt-2 w-full rounded-xl border shadow-xl overflow-hidden z-10 ${
                      isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                    }`}
                  >
                    {CONCEPT_CATEGORIES.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setActiveFilter(category.id);
                        setIsDropdownOpen(false);
                      }}
                        className={`w-full text-left px-4 py-3 text-sm font-semibold transition-colors ${
                        activeFilter === category.id
                          ? 'bg-[#AC5757] text-white'
                            : isDarkMode
                              ? 'text-gray-200 hover:bg-gray-800'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {filteredConcepts.length > 0 && (
            <div
              className={`rounded-2xl mb-10 overflow-hidden border ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
            <div className="md:flex">
              <div className="md:w-1/2 aspect-video md:aspect-auto">
                  <img src={filteredConcepts[0].image} alt={filteredConcepts[0].title} className="w-full h-full object-cover" />
              </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center gap-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="bg-[#AC5757] text-white px-3 py-1 rounded-full text-xs font-semibold">Featured</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyBadge(filteredConcepts[0].difficulty)}`}>
                    {filteredConcepts[0].difficulty}
                  </span>
                </div>
                  <h2 className={`font-judson text-3xl md:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {filteredConcepts[0].title}
                </h2>
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-lg`}>{filteredConcepts[0].subtitle}</p>
                  <div className={`flex items-center gap-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="flex items-center gap-2">
                    <Clock size={16} />
                    {filteredConcepts[0].duration}
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye size={16} />
                    {filteredConcepts[0].views.toLocaleString()} views
                  </div>
                  <div className="flex items-center gap-2">
                    <Star size={16} fill="currentColor" className="text-yellow-500" />
                    {filteredConcepts[0].rating}
                  </div>
                </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {filteredConcepts[0].topics.map((topic, index) => (
                      <span
                        key={index}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDarkMode ? 'bg-gray-900/70 text-gray-200 border border-gray-700' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                      {topic}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => handleConceptClick(filteredConcepts[0])}
                  className="inline-flex items-center gap-2 bg-[#AC5757] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors w-fit"
                >
                  Explore Concept
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredConcepts.slice(1).map((concept) => (
            <div
              key={concept.id}
              onClick={() => handleConceptClick(concept)}
                className={`rounded-2xl border overflow-hidden hover:shadow-xl transition-all duration-200 group cursor-pointer ${
                  isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
            >
                <div className="overflow-hidden aspect-video">
                <img 
                  src={concept.image} 
                  alt={concept.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>
                <div className="p-6 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyBadge(concept.difficulty)}`}>
                    {concept.difficulty}
                  </span>
                    <div className="flex items-center gap-1 text-sm">
                    <Star size={14} fill="currentColor" className="text-yellow-500" />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{concept.rating}</span>
                    </div>
                  </div>
                  <h3 className={`font-judson text-2xl font-bold group-hover:text-[#AC5757] transition-colors ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {concept.title}
                </h3>
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm min-h-[48px]`}>{concept.subtitle}</p>
                  <div className={`flex items-center gap-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="flex items-center gap-1">
                    <Clock size={12} />
                    {concept.duration}
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye size={12} />
                    {concept.views.toLocaleString()}
                  </div>
                </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {concept.topics.slice(0, 2).map((topic, index) => (
                      <span
                        key={index}
                        className={`px-2 py-1 rounded text-xs ${
                          isDarkMode ? 'bg-gray-900/70 text-gray-200 border border-gray-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                      {topic}
                    </span>
                  ))}
                  {concept.topics.length > 2 && (
                      <span className={isDarkMode ? 'text-gray-400 text-xs' : 'text-gray-500 text-xs'}>
                        +{concept.topics.length - 2}
                      </span>
                  )}
                </div>
                <button
                    onClick={(event) => {
                      event.stopPropagation();
                    handleConceptClick(concept);
                  }}
                    className="inline-flex items-center gap-2 bg-[#AC5757] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors w-fit"
                >
                  Explore Concept
                    <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

          <div className="mt-12">
            <div
              className={`rounded-2xl p-8 text-center ${
                isDarkMode ? 'bg-[#AC5757]/20 border border-[#AC5757]/40' : 'bg-gradient-to-r from-[#AC5757] to-[#8A4A4A]'
              }`}
            >
            <div className="flex items-center justify-center mb-4">
              <FolderOpen size={48} className="text-white" />
            </div>
              <h3 className="text-2xl font-bold text-white mb-2">Course Projects</h3>
            <p className="text-white/80 mb-6 max-w-md mx-auto">
              View and manage your course-related projects, assignments, and portfolio work.
            </p>
            <button
              onClick={() => navigate('/app/course-projects')}
              className="bg-white text-[#AC5757] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
            >
              <FolderOpen size={20} />
              View Course Projects
            </button>
          </div>
        </div>

        <div className="text-center mt-12">
          <button 
              onClick={() =>
                alert('Loading more concepts...\n\nThis would fetch additional concepts from the database.')
              }
              className={`px-8 py-3 rounded-lg font-semibold transition-colors border ${
                isDarkMode
                  ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            Load More Concepts
          </button>
          </div>
        </div>
      </div>

      {showConceptModal && selectedConcept && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto ${
              isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'
            }`}
          >
            <div
              className={`sticky top-0 flex items-center justify-between px-6 py-4 border-b ${
                isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
              }`}
            >
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedConcept.title}
              </h2>
                <button 
                  onClick={() => setShowConceptModal(false)}
                className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}
                >
                  ✕
                </button>
              </div>

            <div className="p-6 space-y-6">
              <img src={selectedConcept.image} alt={selectedConcept.title} className="w-full rounded-xl object-cover" />
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{selectedConcept.subtitle}</p>

              <div
                className={`grid sm:grid-cols-3 gap-4 rounded-xl p-4 border ${
                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div>
                  <p className={`text-xs uppercase font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Category
                  </p>
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedConcept.category}
                  </p>
            </div>
                <div>
                  <p className={`text-xs uppercase font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Duration
                  </p>
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedConcept.duration}
                  </p>
              </div>
                <div>
                  <p className={`text-xs uppercase font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Difficulty
                  </p>
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {selectedConcept.difficulty}
                  </p>
                </div>
                <div>
                  <p className={`text-xs uppercase font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Views
                  </p>
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedConcept.views.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className={`text-xs uppercase font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Rating
                  </p>
                  <p className={`text-sm font-semibold flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Star size={14} fill="currentColor" className="text-yellow-500" />
                  {selectedConcept.rating}
                  </p>
                </div>
                <div>
                  <p className={`text-xs uppercase font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Topics
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedConcept.topics.map((topic, index) => (
                      <span
                        key={index}
                        className={`px-3 py-1 rounded-full text-xs ${
                          isDarkMode ? 'bg-gray-900/70 text-gray-200 border border-gray-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                    {topic}
                  </span>
                ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button 
                  onClick={() => navigate(`/app/course-projects?concept=${selectedConcept.slug}`)}
                  className="inline-flex items-center gap-2 bg-[#AC5757] text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
                >
                  Explore related projects
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Concepts;

